import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  FileText, 
  Loader2, 
  Eye, 
  ArrowRight,
  Trash2,
  FileCheck,
  Filter,
  Paperclip
} from 'lucide-react';
import { toast } from 'sonner';

const WebFormsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedForm, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch forms for filter dropdown
      const formsRes = await api().get('/forms');
      setForms(formsRes.data.forms);

      // Fetch submissions
      let url = '/form-submissions';
      const params = [];
      if (selectedForm && selectedForm !== 'all') {
        params.push(`form_id=${selectedForm}`);
      }
      if (statusFilter && statusFilter !== 'all') {
        params.push(`status=${statusFilter}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const submissionsRes = await api().get(url);
      setSubmissions(submissionsRes.data.submissions);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const convertToApplication = async (submissionId) => {
    setConverting(true);
    try {
      const response = await api().post(`/form-submissions/${submissionId}/convert`);
      toast.success('Converted to application successfully!');
      setViewingSubmission(null);
      fetchData();
      // Navigate to the new application
      navigate(`/applications/${response.data.application_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await api().delete(`/form-submissions/${submissionId}`);
      toast.success('Submission deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const renderSubmissionData = (data) => {
    const items = [];
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = data[key];
      items.push(
        <div key={key} className="flex items-start gap-2 py-2 border-b last:border-0">
          <span className="text-sm text-muted-foreground min-w-[120px] capitalize">
            {key.replace(/_/g, ' ')}:
          </span>
          <span className="text-sm font-medium flex-1">{value || '-'}</span>
        </div>
      );
    }
    return items;
  };

  const renderDocumentsList = (documents) => {
    const items = [];
    for (let i = 0; i < documents.length; i++) {
      items.push(
        <div key={i} className="flex items-center gap-2 text-sm">
          <Paperclip className="w-4 h-4 text-muted-foreground" />
          <span>Document {i + 1}</span>
        </div>
      );
    }
    return items;
  };

  const renderSubmissionsList = () => {
    const items = [];
    for (let i = 0; i < submissions.length; i++) {
      const sub = submissions[i];
      const docCount = sub.documents?.length || 0;
      items.push(
        <TableRow key={sub.id} data-testid={`submission-row-${sub.id}`}>
          <TableCell className="font-medium">
            {sub.data?.full_name || sub.data?.student_name || 
             `${sub.data?.first_name || ''} ${sub.data?.last_name || ''}`.trim() || 
             'Unknown'}
          </TableCell>
          <TableCell>{sub.data?.email || sub.data?.student_email || '-'}</TableCell>
          <TableCell>{sub.form_name}</TableCell>
          <TableCell>
            {docCount > 0 ? (
              <Badge variant="outline" className="gap-1">
                <Paperclip className="w-3 h-3" />
                {docCount}
              </Badge>
            ) : '-'}
          </TableCell>
          <TableCell>
            <Badge variant={sub.status === 'converted' ? 'default' : 'secondary'}>
              {sub.status}
            </Badge>
          </TableCell>
          <TableCell className="text-muted-foreground">
            {formatDate(sub.created_at)}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewingSubmission(sub)}
                title="View Details"
              >
                <Eye className="w-4 h-4" />
              </Button>
              {sub.status !== 'converted' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => convertToApplication(sub.id)}
                  title="Convert to Application"
                  className="text-green-600"
                >
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              {sub.status === 'converted' && sub.application_id && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/applications/${sub.application_id}`)}
                  title="View Application"
                >
                  <FileCheck className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={() => deleteSubmission(sub.id)}
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
      );
    }
    return items;
  };

  const renderFormOptions = () => {
    const items = [<SelectItem key="all" value="all">All Forms</SelectItem>];
    for (let i = 0; i < forms.length; i++) {
      const form = forms[i];
      items.push(
        <SelectItem key={form.id} value={form.id}>{form.name}</SelectItem>
      );
    }
    return items;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Web Form Submissions
          </h1>
          <p className="text-muted-foreground">View and convert form submissions to applications</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={selectedForm} onValueChange={setSelectedForm}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by form" />
            </SelectTrigger>
            <SelectContent>{renderFormOptions()}</SelectContent>
          </Select>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No submissions yet</h3>
              <p className="text-sm text-muted-foreground">
                Form submissions will appear here when visitors fill out your published forms
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Form</TableHead>
                  <TableHead>Docs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderSubmissionsList()}</TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={!!viewingSubmission} onOpenChange={() => setViewingSubmission(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
            <DialogDescription>
              Form: {viewingSubmission?.form_name}
            </DialogDescription>
          </DialogHeader>
          
          {viewingSubmission && (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-1">
                {renderSubmissionData(viewingSubmission.data)}
              </div>
              
              {viewingSubmission.documents?.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-sm mb-2">Attached Documents</h4>
                  <div className="space-y-2">
                    {renderDocumentsList(viewingSubmission.documents)}
                  </div>
                </div>
              )}
            </ScrollArea>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingSubmission(null)}>
              Close
            </Button>
            {viewingSubmission?.status !== 'converted' && (
              <Button 
                onClick={() => convertToApplication(viewingSubmission.id)}
                disabled={converting}
              >
                {converting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Convert to Application
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebFormsPage;
